import type { ConversationDigestApiModel as ConversationSummary } from "@noa/api-core";
import { Heart, MoreVertical, Pencil, Trash2, Undo2 } from "lucide-react";
import cn from "classnames";
import { useState, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useLikeConversation } from "@noa/api-client";
import { useRestoreConversation } from "@noa/api-client";
import { formatRelativeTime, getHighlightRanges, useConversationTitle, useIsActiveConversation } from "@noa/ui";
import { Button, Card, Dropdown, Text } from "~/components/ui";
import { DeleteConversationModal } from "./delete-conversation-modal";
import { RenameConversationModal } from "./rename-conversation-modal";

interface ConversationHistoryItemProps {
  conversationSummary: ConversationSummary;
  deleted?: boolean;
  search?: string;
}

export const ConversationHistoryItem: FunctionComponent<ConversationHistoryItemProps> = ({
  conversationSummary,
  deleted = false,
  search,
}) => {
  const { t } = useTranslation("conversation");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const { mutate: restoreConversation, isPending: isRestoring } = useRestoreConversation();
  const { mutate: likeConversation, isPending: isLiking } = useLikeConversation();
  const navigate = useNavigate();

  const isActive = useIsActiveConversation(conversationSummary);
  const messageCountLabel = t("messageCount", { count: conversationSummary.message_count });
  const lastMessageAtLabel = formatRelativeTime(conversationSummary.last_message_at);
  const { title: conversationTitle, dateTitle } = useConversationTitle(conversationSummary);
  const highlightRanges = getHighlightRanges(conversationTitle, search ?? "");

  return (
    <>
      <Card
        size="small"
        className={cn("mb-2", !deleted && isActive ? "border-r-[3px] bg-noa-hover" : "bg-transparent")}
        hoverable={!deleted}
        onClick={!deleted ? () => navigate(`/conversation/${conversationSummary.session_id}`) : undefined}
        bodyClassName="p-3"
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            {!deleted && (
              <Button
                size="small"
                variant="text"
                className="w-4 shrink-0 -ml-2.5"
                icon={
                  conversationSummary.liked ? (
                    <Heart fill="var(--info)" color="var(--info)" size={14} />
                  ) : (
                    <Heart className="text-noa-muted" size={14} />
                  )
                }
                loading={isLiking}
                title={conversationSummary.liked ? t("actions.unlike") : t("actions.like")}
                onClick={(e) => {
                  e.stopPropagation();
                  likeConversation({ sessionId: conversationSummary.session_id, liked: !conversationSummary.liked });
                }}
              />
            )}
            <Text
              strong
              ellipsis
              title={conversationTitle}
              className={cn("min-w-0 flex-1 text-sm", !deleted && isActive ? "text-noa" : "text-noa-muted")}
            >
              {highlightRanges.map((range, index) =>
                range.highlighted ? <mark key={index}>{range.text}</mark> : <span key={index}>{range.text}</span>
              )}
            </Text>

            {deleted ? (
              <Button
                size="small"
                variant="text"
                className="w-4 shrink-0"
                icon={<Undo2 size={14} />}
                loading={isRestoring}
                title={t("actions.restore")}
                onClick={(e) => {
                  e.stopPropagation();
                  restoreConversation(conversationSummary.session_id);
                }}
              />
            ) : (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "rename",
                      icon: <Pencil size={14} />,
                      label: t("actions.rename"),
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        setIsRenameModalOpen(true);
                      },
                    },
                    {
                      key: "delete",
                      icon: <Trash2 size={14} />,
                      label: t("actions.delete"),
                      danger: true,
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        setIsDeleteModalOpen(true);
                      },
                    },
                  ],
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  className="w-4 shrink-0"
                  icon={<MoreVertical size={14} />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            )}
          </div>

          {conversationSummary.name && (
            <Text tone="muted" ellipsis title={dateTitle} className="text-xs">
              {dateTitle}
            </Text>
          )}

          <Text tone="muted" className="text-xs">
            {`${messageCountLabel} - ${lastMessageAtLabel}`}
          </Text>
        </div>
      </Card>

      {!deleted && (
        <>
          <DeleteConversationModal
            isOpen={isDeleteModalOpen}
            setIsOpen={setIsDeleteModalOpen}
            conversation={conversationSummary}
          />

          <RenameConversationModal
            isOpen={isRenameModalOpen}
            setIsOpen={setIsRenameModalOpen}
            conversation={conversationSummary}
          />
        </>
      )}
    </>
  );
};
